const isLocal = 
    window.location.hostname === 'localhost' || 
    window.location.hostname === '127.0.0.1' || 
    window.location.hostname.startsWith('192.168.') || 
    window.location.hostname.startsWith('10.') || 
    window.location.hostname.startsWith('172.');

const SERVER_URL = isLocal
    ? `http://${window.location.hostname}:5003`
    : 'https://el.learninghub.ind.in';

const BASE_URL = `${SERVER_URL}/api`;
export const UPLOAD_URL = `${SERVER_URL}/uploads`;

/**
 * Normalizes and safely encodes media URLs (images, videos, documents).
 * Handles relative paths, absolute URLs, spaces, and special characters.
 */
export const getMediaUrl = (pathOrUrl) => {
    if (!pathOrUrl) return '';
    if (typeof pathOrUrl !== 'string') return pathOrUrl;
    if (pathOrUrl.startsWith('data:') || pathOrUrl.startsWith('blob:')) {
        return pathOrUrl;
    }
    let url = pathOrUrl.trim();
    if (!url) return '';

    // If it's a YouTube URL or already absolute HTTP(S) URL
    if (url.startsWith('http://') || url.startsWith('https://')) {
        try {
            return encodeURI(decodeURI(url));
        } catch (e) {
            return encodeURI(url);
        }
    }

    // Strip leading uploads/ or /uploads/ or /
    const cleanPath = url.replace(/^\/?uploads\//, '').replace(/^\//, '');
    const fullUrl = `${UPLOAD_URL}/${cleanPath}`;
    try {
        return encodeURI(decodeURI(fullUrl));
    } catch (e) {
        return encodeURI(fullUrl);
    }
};

/**
 * Common fetch wrapper to handle errors and headers
 */
const apiFetch = async (endpoint, options = {}) => {
    const token = localStorage.getItem('token');

    const headers = {
        ...options.headers,
    };

    // If body is FormData, don't set Content-Type header (browser will set it with boundary)
    if (!(options.body instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
    }

    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    try {
        const response = await fetch(`${BASE_URL}${endpoint}`, {
            ...options,
            headers,
        });

        const data = await response.json();

        if (!response.ok) {
            if (response.status === 401) {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
            }
            throw new Error(data.message || 'Something went wrong');
        }

        return data;
    } catch (error) {
        console.error('API Error:', error);
        throw error;
    }
};

// Authentication APIs
export const login = (credentials) => {
    return apiFetch('/login', {
        method: 'POST',
        body: JSON.stringify(credentials),
    });
};

export const signup = (userData) => {
    return apiFetch('/signup', {
        method: 'POST',
        body: JSON.stringify(userData),
    });
};

export const sendSignupOtp = (userData) => {
    return apiFetch('/send-signup-otp', {
        method: 'POST',
        body: JSON.stringify(userData),
    });
};

export const verifySignupOtp = (data) => {
    return apiFetch('/verify-signup-otp', {
        method: 'POST',
        body: JSON.stringify(data),
    });
};

export const resendSignupOtp = (email) => {
    return apiFetch('/resend-signup-otp', {
        method: 'POST',
        body: JSON.stringify({ email }),
    });
};

export const sendForgotPasswordOtp = (email) => {
    return apiFetch('/send-forgot-password-otp', {
        method: 'POST',
        body: JSON.stringify({ email }),
    });
};

export const resetPasswordWithOtp = (data) => {
    return apiFetch('/reset-password-with-otp', {
        method: 'POST',
        body: JSON.stringify(data),
    });
};

export const resendForgotPasswordOtp = (email) => {
    return apiFetch('/resend-forgot-password-otp', {
        method: 'POST',
        body: JSON.stringify({ email }),
    });
};

export const logout = () => {
    return apiFetch('/logout', {
        method: 'POST',
    });
};

export const socialLogin = (socialData) => {
    return apiFetch('/oauth/social', {
        method: 'POST',
        body: JSON.stringify(socialData),
    });
};

export const googleOAuthLogin = (googlePayload) => {
    return apiFetch('/oauth/google', {
        method: 'POST',
        body: JSON.stringify(googlePayload),
    });
};

let lastTrackedVisit = { key: '', timestamp: 0 };

export const trackPageVisit = (visitData) => {
    const visitKey = `${visitData.page_name || ''}_${visitData.page_url || ''}`;
    const now = Date.now();
    if (lastTrackedVisit.key === visitKey && (now - lastTrackedVisit.timestamp) < 4000) {
        return Promise.resolve({ success: true, debounced: true });
    }
    lastTrackedVisit = { key: visitKey, timestamp: now };

    return apiFetch('/track-visit', {
        method: 'POST',
        body: JSON.stringify(visitData),
    }).catch(err => console.error("Visitor tracking suppressed error:", err));
};

export const getVisitorLogs = (date = '') => {
    const url = date ? `/admin/visitor-logs?date=${encodeURIComponent(date)}` : '/admin/visitor-logs';
    return apiFetch(url);
};

// Course APIs
export const getCourses = async (all = false) => {
    const url = all ? '/courses?all=true' : '/courses';
    const response = await apiFetch(url);
    if (response.success && response.data) {
        response.data = response.data.map(mapCourseData);
    }
    return response;
};

export const getCourseById = async (id) => {
    const response = await apiFetch(`/courses/${id}`);
    if (response.success && response.data) {
        response.data = mapCourseData(response.data);
    }
    return response;
};

export const searchCourses = async (query) => {
    const response = await apiFetch(`/courses/search?q=${encodeURIComponent(query)}`);
    if (response.success && response.data) {
        response.data = response.data.map(mapCourseData);
    }
    return response;
};

// Helper function to map snake_case from DB to camelCase for frontend
const mapCourseData = (course) => {
    return {
        ...course,
        originalPrice: course.original_price,
        ratingCount: course.rating_count,
        students: course.students_count,
        lectures: course.lectures_count,
        lastUpdated: course.last_updated,
        isPublished: course.is_published,
        articles: course.articles_count,
        resources: course.resources_count,
        fullDescription: course.full_description,
        instructorDetails: {
            name: course.instructor_name,
            title: course.instructor_title,
            avatar: course.instructor_avatar,
            bio: course.instructor_bio
        },
        hasHardcopyCertificate: Boolean(course.has_hardcopy_certificate),
        certificateTitle: course.certificate_title || 'Apply for NCVET Assessment - Get Your Hardcopy ASDC Certificate',
        certificatePrice: course.certificate_price || 499,
        whatYouLearn: course.whatYouLearn || course.learning_points || [],
        studyMaterials: course.studyMaterials || course.study_materials || [],
    };
};


// Admin APIs
export const createCourse = (courseData) => {
    return apiFetch('/courses', {
        method: 'POST',
        body: JSON.stringify(courseData),
    });
};

export const updateCourse = (id, courseData) => {
    return apiFetch(`/courses/${id}`, {
        method: 'PUT',
        body: JSON.stringify(courseData),
    });
};

export const deleteCourse = (id) => {
    return apiFetch(`/courses/${id}`, {
        method: 'DELETE',
    });
};

export const deleteLecture = (lectureId) => {
    return apiFetch(`/lectures/${lectureId}`, {
        method: 'DELETE',
    });
};

export const deleteUploadedFile = (fileUrl) => {
    return apiFetch('/delete-file', {
        method: 'DELETE',
        body: JSON.stringify({ fileUrl }),
    });
};

export const uploadCourseImage = (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiFetch('/upload', {
        method: 'POST',
        body: formData,
    });
};

export const getStatistics = () => {
    return apiFetch('/statistics');
};

export const enrollCourse = (enrollData) => {
    return apiFetch('/enroll', {
        method: 'POST',
        body: JSON.stringify(enrollData),
    });
};

export const getEnrollmentStats = () => {
    return apiFetch('/admin/enrollments');
};

export const getSettings = () => {
    return apiFetch('/settings');
};

export const updateSettings = (settingsData) => {
    return apiFetch('/admin/settings', {
        method: 'POST',
        body: JSON.stringify(settingsData),
    });
};

export const updateFavicon = (iconUrl) => {
    if (!iconUrl) return;
    try {
        const links = document.querySelectorAll("link[rel*='icon']");
        links.forEach(link => {
            link.href = iconUrl;
        });
        if (links.length === 0) {
            const newLink = document.createElement('link');
            newLink.rel = 'icon';
            newLink.href = iconUrl;
            document.head.appendChild(newLink);
        }
    } catch (e) {
        console.error("Failed to update favicon link:", e);
    }
};

export const uploadFile = (file, onProgress = null) => {
    const CHUNK_SIZE = 900 * 1024; // 900KB per chunk (under Nginx 1MB default)
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const startTime = Date.now();
    let uploadedBytes = 0;

    return new Promise(async (resolve, reject) => {
        const token = localStorage.getItem('token');

        try {
            for (let i = 0; i < totalChunks; i++) {
                const start = i * CHUNK_SIZE;
                const end = Math.min(start + CHUNK_SIZE, file.size);
                const chunk = file.slice(start, end);

                const formData = new FormData();
                formData.append('chunk', chunk, file.name);
                formData.append('filename', file.name);
                formData.append('chunkIndex', i.toString());
                formData.append('totalChunks', totalChunks.toString());

                const response = await fetch(`${BASE_URL}/upload-chunk`, {
                    method: 'POST',
                    headers: token ? { 'Authorization': `Bearer ${token}` } : {},
                    body: formData
                });

                const result = await response.json();

                if (!response.ok || !result.success) {
                    throw new Error(result.message || 'Chunk upload failed');
                }

                uploadedBytes += (end - start);

                // Report progress
                if (onProgress) {
                    const percentComplete = Math.round((uploadedBytes / file.size) * 100);
                    const elapsedSec = (Date.now() - startTime) / 1000;
                    const speedBps = elapsedSec > 0 ? uploadedBytes / elapsedSec : 0;
                    const remainingBytes = file.size - uploadedBytes;
                    const etaSec = speedBps > 0 ? remainingBytes / speedBps : 0;
                    onProgress(percentComplete, {
                        loaded: uploadedBytes,
                        total: file.size,
                        speed: speedBps,
                        eta: etaSec
                    });
                }

                // Last chunk returns the final file info
                if (result.complete) {
                    resolve(result);
                    return;
                }
            }
        } catch (error) {
            reject(error);
        }
    });
};

export const getUserEnrollments = () => {
    return apiFetch('/user/enrollments');
};

export const markVideoWatched = (data) => {
    return apiFetch('/video-watched', {
        method: 'POST',
        body: JSON.stringify(data)
    });
};

export const getCourseProgress = (courseId) => {
    return apiFetch(`/courses/${courseId}/progress`);
};

export const getUserProfile = () => {
    return apiFetch('/user/profile');
};

export const updateUserProfile = (profileData) => {
    return apiFetch('/user/profile', {
        method: 'PUT',
        body: JSON.stringify(profileData)
    });
};

// Student Management APIs (Admin)
export const getAdminStudents = () => {
    return apiFetch('/admin/students');
};

export const createAdminStudent = (studentData) => {
    return apiFetch('/admin/students', {
        method: 'POST',
        body: JSON.stringify(studentData),
    });
};

export const getAdminDepartments = () => {
    return apiFetch('/admin/departments');
};

export const updateAdminStudent = (id, studentData) => {
    return apiFetch(`/admin/students/${id}`, {
        method: 'PUT',
        body: JSON.stringify(studentData),
    });
};

export const updateAdminStudentStatus = (id, status) => {
    return apiFetch(`/admin/students/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
    });
};

export const deleteAdminStudent = (id) => {
    return apiFetch(`/admin/students/${id}`, {
        method: 'DELETE',
    });
};

export const getAdminStudentEnrollments = (studentId) => {
    return apiFetch(`/admin/students/${studentId}/enrollments`);
};

export const assignAdminStudentCourses = (studentId, courseIds, isPaid = false) => {
    return apiFetch(`/admin/students/${studentId}/enrollments`, {
        method: 'POST',
        body: JSON.stringify({ course_ids: courseIds, is_paid: isPaid }),
    });
};

export const unassignAdminStudentCourse = (studentId, courseId) => {
    return apiFetch(`/admin/students/${studentId}/enrollments/${courseId}`, {
        method: 'DELETE',
    });
};

export const getCourseExam = (courseId, refresh = false) => {
    return apiFetch(`/courses/${courseId}/exam${refresh ? '?refresh=true' : ''}`);
};

export const submitCourseExam = (courseId, answers) => {
    return apiFetch(`/courses/${courseId}/exam/submit`, {
        method: 'POST',
        body: JSON.stringify({ answers })
    });
};

export const getCourseExamAttempts = (courseId) => {
    return apiFetch(`/courses/${courseId}/attempts`);
};

// Flash Sale API Calls
export const getFlashSale = () => {
    return apiFetch('/flash-sale');
};

export const getAdminFlashSales = () => {
    return apiFetch('/admin/flash-sale');
};

export const createFlashSale = (data) => {
    return apiFetch('/admin/flash-sale', {
        method: 'POST',
        body: JSON.stringify(data),
    });
};

export const deleteFlashSale = () => {
    return apiFetch('/admin/flash-sale', {
        method: 'DELETE',
    });
};

// Payment API Calls
export const createPaymentSession = (courseId, returnUrl) => {
    return apiFetch('/payment/create', {
        method: 'POST',
        body: JSON.stringify({ courseId, returnUrl })
    });
};

export const verifyPayment = (orderId) => {
    return apiFetch('/payment/verify', {
        method: 'POST',
        body: JSON.stringify({ orderId })
    });
};

export const applyHardcopyCertificate = (applicationData) => {
    return apiFetch('/apply-hardcopy-certificate', {
        method: 'POST',
        body: JSON.stringify(applicationData)
    });
};

export const getAdminCertificateOrders = () => {
    return apiFetch('/admin/certificate-orders');
};

// Instructor Application API Calls
export const submitInstructorApplication = (formData) => {
    return apiFetch('/instructor-applications', {
        method: 'POST',
        body: JSON.stringify(formData)
    });
};

export const getAdminInstructorApplications = () => {
    return apiFetch('/admin/instructor-applications');
};

export const updateInstructorApplicationStatus = (id, status) => {
    return apiFetch(`/admin/instructor-applications/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ status })
    });
};

export const deleteInstructorApplication = (id) => {
    return apiFetch(`/admin/instructor-applications/${id}`, {
        method: 'DELETE'
    });
};

// Contact Messages API Calls
export const submitContactMessage = (formData) => {
    return apiFetch('/contact-messages', {
        method: 'POST',
        body: JSON.stringify(formData)
    });
};

export const getAdminContactMessages = () => {
    return apiFetch('/admin/contact-messages');
};

export const updateContactMessageStatus = (id, status) => {
    return apiFetch(`/admin/contact-messages/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ status })
    });
};

export const deleteContactMessage = (id) => {
    return apiFetch(`/admin/contact-messages/${id}`, {
        method: 'DELETE'
    });
};

// Cashfree Hardcopy Certificate API Calls
export const createCertificatePaymentSession = (formData) => {
    return apiFetch('/create-certificate-payment-session', {
        method: 'POST',
        body: JSON.stringify(formData)
    });
};

export const verifyCertificatePayment = (data) => {
    return apiFetch('/verify-certificate-payment', {
        method: 'POST',
        body: JSON.stringify(data)
    });
};

export const getCertificateOrderDetails = (orderRef) => {
    return apiFetch(`/certificate-orders/by-reference?order_ref=${encodeURIComponent(orderRef)}`);
};


